// Auto-calibration: determine the pixel → meter scale factor using the rider's
// own body dimensions from MediaPipe's 3D world landmarks, not population
// averages. No user input required.
//
// Pipeline per frame:
//   1. Measure actual body segment lengths in metres from worldLandmarks
//      (MediaPipe estimates real-world positions with origin at the hip).
//   2. Measure the same segments in 2D image pixels from landmarks.
//   3. Derive px_per_m = 2d_px / 3d_metres for each segment.
//   4. Pick the highest-confidence estimate (best visibility + reasonable size).
//   5. Compute camera angle correction: cos(θ) where θ is estimated from the
//      ratio of 2D shoulder width to 3D shoulder width.
//   6. Cross-validate all segment-derived scales — if they agree within 8%,
//      calibration is high-confidence.
//
// An external EMA smoother (in useVisionPipeline) stabilises the scale over
// time. The world-landmark-derived body dimensions are also smoothed with
// α=0.05 since they should be constant for a given rider.

import { Landmark, WorldLandmark } from './poseDetector';

const LM = {
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  // Wave 20.49v — legs added to the calibration ensemble. Thigh
  // (hip→knee) and shin (knee→ankle) barely foreshorten across
  // aero / hoods / upright, so they anchor the scale when the
  // torso + shoulder segments partially fold under aero. Without
  // them, hoods position was reading HIGHER drag than upright
  // no-hands (impossible) because the shoulder-only scale
  // ensemble drifted with rider lean.
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
};

export type CalibrationSource =
  | 'shoulder'
  | 'hip'
  | 'torso'
  | 'upper_arm'
  | 'thigh'
  | 'shin'
  | 'none';

export interface CalibrationResult {
  /** Pixels per metre — Track 1.2 emits a confidence-weighted ensemble of
   *  all visible body segments, not just the single highest-confidence one.
   *  When one segment becomes occluded mid-session the px/m no longer flips. */
  px_per_m: number;
  /** Which body segment dominated this frame's ensemble (highest weight).
   *  Kept for UI display; the actual scale is the weighted mean. */
  source: CalibrationSource;
  /** 0..1 — overall confidence in the calibration (sum of input weights
   *  scaled by calibration_health). */
  confidence: number;
  /** Camera angle correction factor: multiply frontal area by this to
   *  compensate for the camera not being perpendicular to the rider.
   *  1.0 = perfectly frontal, <1.0 = angled (area is foreshortened). */
  angle_correction: number;
  /** 0..1 — how well the multiple segment-derived scales agree with each
   *  other. High = all segments give similar px_per_m. */
  calibration_health: number;
  /** The 3D real-world segment length of the dominant source (metres). */
  ref_m: number;
  /** The 2D pixel distance of the dominant source. */
  ref_px: number;
  /** Track 1.2 — health-gated flag. When false, baselineRef should reject
   *  this frame entirely (don't drift the running scale, don't lock the
   *  baseline on it). Default threshold: calibration_health >= 0.4. */
  is_usable: boolean;
  /** Wave 13 / Track E — once the rider's body dimensions have been stable
   *  for ~2 s (CoV < 0.02 on every measured dim for 60 consecutive frames),
   *  we snapshot px_per_m and stop drifting it. Prevents slow off-axis
   *  drift over a long session. SMPL `bone_offsets` are also frozen — the
   *  fitter reads `isCalibrationLocked()` from this module. */
  calibration_locked: boolean;
}

const MIN_VIS = 0.55;

// ---- Smoothed world-landmark body dimensions ----
// These are EMA-smoothed across frames because a rider's body dimensions
// don't change during a session. The smoothing kills pose-estimation noise.
const BODY_SMOOTH_ALPHA = 0.05;

let _smoothedShoulderM = 0;
let _smoothedHipM = 0;
let _smoothedTorsoM = 0;
let _smoothedUpperArmM = 0;
let _smoothedThighM = 0;
let _smoothedShinM = 0;
let _initCount = 0;

// Wave 13 / Track E — calibration lock state.
// Tracks the last RAW_DIM_WINDOW frames of dist3d measurements for each
// of shoulder / hip / torso / upper_arm. When every dim's coefficient of
// variation across the window is below LOCK_COV_THRESHOLD for
// LOCK_STABLE_FRAMES consecutive frames, lock and snapshot px_per_m.
const RAW_DIM_WINDOW = 120; // ~4 s at 30 Hz
const LOCK_STABLE_FRAMES = 60; // ~2 s of sustained low variance
const LOCK_COV_THRESHOLD = 0.02; // 2% coefficient of variation
type RawDims = { shoulder: number; hip: number; torso: number; upper_arm: number };
let _rawDimsRing: RawDims[] = [];
let _rawDimsCount = 0;
let _lockStableCount = 0;
let _calibrationLocked = false;
let _lockedPxPerM = 0;
// Diagnostic: last-logged is_usable so we only console.log on transitions.
let _lastLoggedUsable: boolean | null = null;

function pushRawDim(dim: RawDims): void {
  if (_rawDimsRing.length < RAW_DIM_WINDOW) {
    _rawDimsRing.push(dim);
  } else {
    _rawDimsRing[_rawDimsCount % RAW_DIM_WINDOW] = dim;
  }
  _rawDimsCount++;
}

function covOfDim(values: number[]): number {
  if (values.length < 2) return Infinity;
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  if (mean <= 0) return Infinity;
  const variance =
    values.reduce((s, v) => s + (v - mean) * (v - mean), 0) / values.length;
  return Math.sqrt(variance) / mean;
}

function allDimsStable(): boolean {
  if (_rawDimsRing.length < RAW_DIM_WINDOW) return false;
  const dims: Array<keyof RawDims> = ['shoulder', 'hip', 'torso', 'upper_arm'];
  for (const k of dims) {
    const vals = _rawDimsRing.map((r) => r[k]).filter((v) => v > 0);
    if (vals.length < RAW_DIM_WINDOW * 0.8) return false;
    if (covOfDim(vals) > LOCK_COV_THRESHOLD) return false;
  }
  return true;
}

/** Wave 13 / Track E — module-level getter the SMPL fitter reads to know
 *  whether to freeze `bone_offsets`. Resets via `resetCalibration()`. */
// Wave 20.49bz — Expose smoothed body dims for the frontal-area
// projector so it can width the capsules per-rider. Returns null
// values before the EMA has enough samples to be meaningful.
export function getSmoothedBodyDimsM(): {
  shoulder_width: number | null;
  hip_width: number | null;
  torso_length: number | null;
  upper_arm: number | null;
} {
  if (_initCount < 30) {
    return {
      shoulder_width: null,
      hip_width: null,
      torso_length: null,
      upper_arm: null,
    };
  }
  return {
    shoulder_width: _smoothedShoulderM > 0 ? _smoothedShoulderM : null,
    hip_width: _smoothedHipM > 0 ? _smoothedHipM : null,
    torso_length: _smoothedTorsoM > 0 ? _smoothedTorsoM : null,
    upper_arm: _smoothedUpperArmM > 0 ? _smoothedUpperArmM : null,
  };
}

export function isCalibrationLocked(): boolean {
  return _calibrationLocked;
}

function ema(prev: number, next: number): number {
  if (prev <= 0) return next;
  return prev * (1 - BODY_SMOOTH_ALPHA) + next * BODY_SMOOTH_ALPHA;
}

function dist2d(a: Landmark, b: Landmark, w: number, h: number): number {
  return Math.hypot((a.x - b.x) * w, (a.y - b.y) * h);
}

function dist3d(a: WorldLandmark, b: WorldLandmark): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function visible(...lms: Array<Landmark | WorldLandmark>): boolean {
  return lms.every((l) => (l.visibility ?? 0) >= MIN_VIS);
}

/**
 * Run calibration using the rider's own body dimensions from 3D world
 * landmarks. No population averages, no user input.
 */
export function calibrate(
  landmarks: Landmark[] | null,
  worldLandmarks: WorldLandmark[] | null,
  imageWidth: number,
  imageHeight: number
): CalibrationResult {
  if (
    !landmarks ||
    !worldLandmarks ||
    landmarks.length < 33 ||
    worldLandmarks.length < 33
  ) {
    return zeroResult();
  }

  // ---- Step 1: measure actual body dimensions from 3D world landmarks ----
  const lSh2 = landmarks[LM.LEFT_SHOULDER];
  const rSh2 = landmarks[LM.RIGHT_SHOULDER];
  const lSh3 = worldLandmarks[LM.LEFT_SHOULDER];
  const rSh3 = worldLandmarks[LM.RIGHT_SHOULDER];

  const lHip2 = landmarks[LM.LEFT_HIP];
  const rHip2 = landmarks[LM.RIGHT_HIP];
  const lHip3 = worldLandmarks[LM.LEFT_HIP];
  const rHip3 = worldLandmarks[LM.RIGHT_HIP];

  const lElb2 = landmarks[LM.LEFT_ELBOW];
  const lElb3 = worldLandmarks[LM.LEFT_ELBOW];
  const rElb2 = landmarks[LM.RIGHT_ELBOW];
  const rElb3 = worldLandmarks[LM.RIGHT_ELBOW];

  // Wave 20.49v — leg endpoints for thigh + shin segments.
  const lKne2 = landmarks[LM.LEFT_KNEE];
  const rKne2 = landmarks[LM.RIGHT_KNEE];
  const lKne3 = worldLandmarks[LM.LEFT_KNEE];
  const rKne3 = worldLandmarks[LM.RIGHT_KNEE];
  const lAnk2 = landmarks[LM.LEFT_ANKLE];
  const rAnk2 = landmarks[LM.RIGHT_ANKLE];
  const lAnk3 = worldLandmarks[LM.LEFT_ANKLE];
  const rAnk3 = worldLandmarks[LM.RIGHT_ANKLE];

  // Wave 13 / Track E — capture raw dist3d before EMA, so the lock detector
  // sees real measurement variance (the EMA-smoothed values barely move).
  const rawShoulder = visible(lSh3, rSh3) ? dist3d(lSh3, rSh3) : 0;
  const rawHip = visible(lHip3, rHip3) ? dist3d(lHip3, rHip3) : 0;
  const rawTorso = visible(lSh3, lHip3)
    ? dist3d(lSh3, lHip3)
    : visible(rSh3, rHip3)
    ? dist3d(rSh3, rHip3)
    : 0;
  const rawUpperArm = visible(lSh3, lElb3)
    ? dist3d(lSh3, lElb3)
    : visible(rSh3, rElb3)
    ? dist3d(rSh3, rElb3)
    : 0;
  // Wave 20.49v — leg segment raw dimensions.
  const rawThigh = visible(lHip3, lKne3)
    ? dist3d(lHip3, lKne3)
    : visible(rHip3, rKne3)
    ? dist3d(rHip3, rKne3)
    : 0;
  const rawShin = visible(lKne3, lAnk3)
    ? dist3d(lKne3, lAnk3)
    : visible(rKne3, rAnk3)
    ? dist3d(rKne3, rAnk3)
    : 0;
  pushRawDim({ shoulder: rawShoulder, hip: rawHip, torso: rawTorso, upper_arm: rawUpperArm });

  // Measure + smooth each body dimension. Once locked, the EMA freezes —
  // any further drift in raw measurements is intentionally ignored.
  if (!_calibrationLocked) {
    if (rawShoulder > 0) _smoothedShoulderM = ema(_smoothedShoulderM, rawShoulder);
    if (rawHip > 0) _smoothedHipM = ema(_smoothedHipM, rawHip);
    if (rawTorso > 0) _smoothedTorsoM = ema(_smoothedTorsoM, rawTorso);
    if (rawUpperArm > 0) _smoothedUpperArmM = ema(_smoothedUpperArmM, rawUpperArm);
    if (rawThigh > 0) _smoothedThighM = ema(_smoothedThighM, rawThigh);
    if (rawShin > 0) _smoothedShinM = ema(_smoothedShinM, rawShin);
  }
  _initCount++;

  // Need at least ~10 frames of data before smoothed dimensions are stable
  if (_initCount < 10 || _smoothedShoulderM <= 0.1) {
    return zeroResult();
  }

  // ---- Step 2: compute px_per_m from each segment ----
  interface Candidate {
    px_per_m: number;
    source: CalibrationSource;
    confidence: number;
    ref_m: number;
    ref_px: number;
  }
  const candidates: Candidate[] = [];

  // Shoulder
  if (visible(lSh2, rSh2) && _smoothedShoulderM > 0.15) {
    const px = dist2d(lSh2, rSh2, imageWidth, imageHeight);
    if (px > 10) {
      candidates.push({
        px_per_m: px / _smoothedShoulderM,
        source: 'shoulder',
        confidence: Math.min(lSh2.visibility ?? 0, rSh2.visibility ?? 0),
        ref_m: _smoothedShoulderM,
        ref_px: px,
      });
    }
  }

  // Hip
  if (visible(lHip2, rHip2) && _smoothedHipM > 0.1) {
    const px = dist2d(lHip2, rHip2, imageWidth, imageHeight);
    if (px > 8) {
      candidates.push({
        px_per_m: px / _smoothedHipM,
        source: 'hip',
        confidence:
          Math.min(lHip2.visibility ?? 0, rHip2.visibility ?? 0) * 0.9,
        ref_m: _smoothedHipM,
        ref_px: px,
      });
    }
  }

  // Torso (shoulder → hip, same side)
  if (_smoothedTorsoM > 0.2) {
    if (visible(lSh2, lHip2)) {
      const px = dist2d(lSh2, lHip2, imageWidth, imageHeight);
      if (px > 15) {
        candidates.push({
          px_per_m: px / _smoothedTorsoM,
          source: 'torso',
          confidence:
            Math.min(lSh2.visibility ?? 0, lHip2.visibility ?? 0) * 0.85,
          ref_m: _smoothedTorsoM,
          ref_px: px,
        });
      }
    } else if (visible(rSh2, rHip2)) {
      const px = dist2d(rSh2, rHip2, imageWidth, imageHeight);
      if (px > 15) {
        candidates.push({
          px_per_m: px / _smoothedTorsoM,
          source: 'torso',
          confidence:
            Math.min(rSh2.visibility ?? 0, rHip2.visibility ?? 0) * 0.85,
          ref_m: _smoothedTorsoM,
          ref_px: px,
        });
      }
    }
  }

  // Upper arm
  if (_smoothedUpperArmM > 0.1) {
    for (const [s2, e2] of [
      [lSh2, lElb2],
      [rSh2, rElb2],
    ] as [Landmark, Landmark][]) {
      if (visible(s2, e2)) {
        const px = dist2d(s2, e2, imageWidth, imageHeight);
        if (px > 10) {
          candidates.push({
            px_per_m: px / _smoothedUpperArmM,
            source: 'upper_arm',
            confidence:
              Math.min(s2.visibility ?? 0, e2.visibility ?? 0) * 0.75,
            ref_m: _smoothedUpperArmM,
            ref_px: px,
          });
        }
      }
    }
  }

  // Wave 20.49v — Thigh (hip → knee). Big weight (0.95) because
  // the femur barely foreshortens between aero/hoods/upright: it
  // sits roughly in the sagittal plane and stays perpendicular
  // to the camera as the rider rotates their torso forward. This
  // anchors the scale ensemble against torso-lean drift that
  // was making aero positions read HIGHER CdA than upright.
  if (_smoothedThighM > 0.15) {
    for (const [h2, k2] of [
      [lHip2, lKne2],
      [rHip2, rKne2],
    ] as [Landmark, Landmark][]) {
      if (visible(h2, k2)) {
        const px = dist2d(h2, k2, imageWidth, imageHeight);
        if (px > 12) {
          candidates.push({
            px_per_m: px / _smoothedThighM,
            source: 'thigh',
            confidence:
              Math.min(h2.visibility ?? 0, k2.visibility ?? 0) * 0.95,
            ref_m: _smoothedThighM,
            ref_px: px,
          });
        }
      }
    }
  }

  // Wave 20.49v — Shin (knee → ankle). Similar rationale to
  // thigh but slightly lower weight (0.80) since ankles are
  // more often clipped by the video frame or clothes.
  if (_smoothedShinM > 0.15) {
    for (const [k2, a2] of [
      [lKne2, lAnk2],
      [rKne2, rAnk2],
    ] as [Landmark, Landmark][]) {
      if (visible(k2, a2)) {
        const px = dist2d(k2, a2, imageWidth, imageHeight);
        if (px > 10) {
          candidates.push({
            px_per_m: px / _smoothedShinM,
            source: 'shin',
            confidence:
              Math.min(k2.visibility ?? 0, a2.visibility ?? 0) * 0.80,
            ref_m: _smoothedShinM,
            ref_px: px,
          });
        }
      }
    }
  }

  if (candidates.length === 0) {
    return zeroResult();
  }

  // ---- Step 3: weighted ensemble of all candidates ----
  // Track 1.2: instead of picking the single highest-confidence segment, we
  // take a confidence-weighted mean of all visible candidates. This kills
  // the discontinuity that used to happen when the "winner" segment briefly
  // dropped below visibility — the px/m no longer flips.
  // The previous "pick best" code path is preserved as the `source` label
  // (which UI uses) and as the `ref_m`/`ref_px` outputs.
  candidates.sort((a, b) => b.confidence - a.confidence);
  const best = candidates[0];
  const totalWeight = candidates.reduce((s, c) => s + c.confidence, 0);
  const ensemble_px_per_m = totalWeight > 0
    ? candidates.reduce((s, c) => s + c.px_per_m * c.confidence, 0) / totalWeight
    : best.px_per_m;

  // ---- Step 4: camera angle correction ----
  // If we have both 2D and 3D shoulder widths, the ratio gives cos(θ)
  // where θ is the angle between the camera axis and the rider's frontal
  // plane. When θ > 0 (camera is off-axis), the frontal area is inflated
  // by 1/cos(θ), so we correct by multiplying area by cos(θ).
  // Track 1.2: use the ensemble scale here, not the single-best scale.
  let angle_correction = 1.0;
  if (
    visible(lSh2, rSh2) &&
    _smoothedShoulderM > 0.15 &&
    ensemble_px_per_m > 0
  ) {
    const shoulder2dPx = dist2d(lSh2, rSh2, imageWidth, imageHeight);
    const shoulder2dM = shoulder2dPx / ensemble_px_per_m;
    const ratio = shoulder2dM / _smoothedShoulderM;
    // Wave 20.35 — clamped to [0.35, 1.0]. Below 0.35 is definitely
    // a side view; between 0.35 and 0.5 is a wide-angle-lens-turned-
    // vertical setup where the shoulder ratio foreshortens hard but
    // the frames are still usable for CdA (the pipeline already applies
    // the correction). Locking users out for their own valid camera
    // choice is worse than letting slightly-off frames through.
    angle_correction = Math.max(0.35, Math.min(1.0, ratio));
  }

  // ---- Step 5: cross-validate calibration health ----
  let calibration_health = 1.0;
  if (candidates.length >= 2) {
    const scales = candidates.map((c) => c.px_per_m);
    const mean = scales.reduce((s, v) => s + v, 0) / scales.length;
    const maxDev = Math.max(
      ...scales.map((v) => Math.abs(v - mean) / mean)
    );
    // If max deviation < 8%, perfect health; > 25%, poor health.
    calibration_health = Math.max(0, Math.min(1, 1 - (maxDev - 0.08) / 0.17));
  }

  // Track 1.2 — gate frames by overall calibration health OR camera angle.
  //
  // Wave 20.35 (2026-08): switched from AND to OR after a rider reported
  // their entire session showed empty data because their wide-angle phone
  // (turned vertical) produced angle_correction ≈ 0.4 continuously.
  // health was fine, angle wasn't — is_usable = false → zero frames
  // streamed → no CdA, no coach, empty session row. The AND-gate assumed
  // both signals had to agree; in practice either one being confident is
  // enough evidence the pipeline should keep going. We still expose both
  // to the banner so a rider knows which lever to nudge.
  //
  // Thresholds also nudged down (health 0.3→0.25, angle 0.5→0.3) so
  // borderline valid setups aren't shut out.
  const HEALTH_MIN = 0.25;
  const ANGLE_MIN = 0.3;
  const health_ok = calibration_health >= HEALTH_MIN;
  const angle_ok = angle_correction >= ANGLE_MIN;
  const is_usable = health_ok || angle_ok;

  // Diagnostic log so we can debug remote customer setups from DevTools.
  // Fires only on transitions (usable ↔ unusable) so it doesn't drown the
  // console; the per-frame reason lets us tell which threshold is biting.
  if (is_usable !== _lastLoggedUsable) {
    const reason = !is_usable
      ? [
          calibration_health < HEALTH_MIN
            ? `health=${calibration_health.toFixed(2)}<${HEALTH_MIN}`
            : null,
          angle_correction < ANGLE_MIN
            ? `angle=${angle_correction.toFixed(2)}<${ANGLE_MIN}`
            : null,
        ]
          .filter(Boolean)
          .join(' ')
      : `health=${calibration_health.toFixed(2)} angle=${angle_correction.toFixed(2)}`;
    // eslint-disable-next-line no-console
    console.log(`[calibration] is_usable=${is_usable} ${reason}`);
    _lastLoggedUsable = is_usable;
  }

  // Wave 13 / Track E — advance the lock state machine. The lock condition
  // is "every dim's CoV across the 4 s window stays below threshold for
  // 2 s straight, while the frame itself is usable". Once locked, we
  // snapshot px_per_m and never recompute it — the fitter also freezes
  // bone_offsets via isCalibrationLocked().
  if (!_calibrationLocked) {
    if (is_usable && allDimsStable()) {
      _lockStableCount++;
      if (_lockStableCount >= LOCK_STABLE_FRAMES) {
        _calibrationLocked = true;
        _lockedPxPerM = ensemble_px_per_m;
      }
    } else {
      _lockStableCount = 0;
    }
  }
  const effective_px_per_m = _calibrationLocked ? _lockedPxPerM : ensemble_px_per_m;

  return {
    px_per_m: effective_px_per_m,
    source: best.source,
    confidence: best.confidence * calibration_health,
    angle_correction,
    calibration_health,
    ref_m: best.ref_m,
    ref_px: best.ref_px,
    is_usable,
    calibration_locked: _calibrationLocked,
  };
}

/** Reset smoothed dimensions (call when session changes). */
export function resetCalibration(): void {
  _smoothedShoulderM = 0;
  _smoothedHipM = 0;
  _smoothedTorsoM = 0;
  _smoothedUpperArmM = 0;
  _smoothedThighM = 0;
  _smoothedShinM = 0;
  _initCount = 0;
  _rawDimsRing = [];
  _rawDimsCount = 0;
  _lockStableCount = 0;
  _calibrationLocked = false;
  _lockedPxPerM = 0;
  _lastLoggedUsable = null;
}

function zeroResult(): CalibrationResult {
  return {
    px_per_m: 0,
    source: 'none',
    confidence: 0,
    angle_correction: 1.0,
    calibration_health: 0,
    ref_m: 0,
    ref_px: 0,
    is_usable: false,
    calibration_locked: _calibrationLocked,
  };
}
